/**
 * Field Section Component
 * Groups fields into collapsible sections with configurable layouts
 */

import React, { useState } from 'react';
import { FieldSchema, ValidationResult, getConfigValue } from '@/config/fieldConfig';
import { SectionConfig } from '@/config/fieldSections';
import { useI18n } from '@/i18n';
import FieldRenderer from './FieldRenderer';

export interface FieldSectionProps {
  sectionKey: string;
  sectionConfig: SectionConfig;
  fields: Array<{ name: string; schema: FieldSchema }>;
  values: Record<string, any>;
  onChange: (name: string, value: any) => void;
  errors?: Record<string, ValidationResult>;
  disabled?: boolean;
  appContext?: any;
  onBrowseTools?: (fieldName: string) => void;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
}

export const FieldSection: React.FC<FieldSectionProps> = ({
  sectionKey,
  sectionConfig,
  fields,
  values,
  onChange,
  errors = {},
  disabled = false,
  appContext,
  onBrowseTools,
  collapsible = true,
  defaultCollapsed = false,
}) => {
  const { getSectionTitle } = useI18n();
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  // Filter out fields that should be hidden based on showIf
  const visibleFields = fields.filter((field) => {
    if (!field.schema.showIf) return true;
    return field.schema.showIf(values);
  });

  // Don't render section if no visible fields
  if (visibleFields.length === 0) {
    return null;
  }

  // Determine grid columns based on section config
  const gridClass = sectionConfig.columns === 2
    ? 'grid grid-cols-1 md:grid-cols-2 gap-4'
    : 'flex flex-col gap-4';

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      {/* Section Header */}
      <div
        className={`
          px-4 py-3 border-b border-gray-200 flex items-center justify-between
          ${collapsible ? 'cursor-pointer hover:bg-gray-50' : ''}
        `}
        onClick={() => collapsible && setIsCollapsed(!isCollapsed)}
      >
        <h3 className="text-lg font-semibold text-gray-800">
          {getSectionTitle(sectionKey) || sectionConfig.title}
        </h3>

        {collapsible && (
          <button
            type="button"
            className="text-gray-500 hover:text-gray-700 transition-transform"
            style={{ transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Section Content */}
      {!isCollapsed && (
        <div className="p-4">
          <div className={gridClass}>
            {visibleFields.map((field) => (
              <FieldRenderer
                key={field.name}
                name={field.name}
                value={getConfigValue(values, field.name)}
                onChange={(value) => onChange(field.name, value)}
                schema={field.schema}
                error={errors[field.name]}
                disabled={disabled}
                appContext={appContext || values}
                onBrowseTools={
                  field.schema.hasToolBrowser && onBrowseTools
                    ? () => onBrowseTools(field.name)
                    : undefined
                }
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FieldSection;
